import { create } from 'zustand'

/**
 * How each outgoing file message is getting on — what its bubble draws over
 * itself: a ring filling while the bytes go, a spinner while the message
 * itself is sent, ↑ to send it again once the user stopped it or it failed.
 *
 * Keyed by the optimistic message's id, which is also the clientMsgId the send
 * carries, so the bubble finds its own entry with nothing passed down to it.
 * The message itself stays in the messenger store's `pendingSends` the whole
 * time; this only says how far along it is. See services/upload-outbox.ts.
 */

/**
 * `stopped` is the user's ✗, `failed` is everything else that went wrong. Both
 * keep the message in the thread with Retry and Delete, as WhatsApp does — a
 * stray tap on the ✗ (it sits in the middle of the video) must not lose a
 * message the user then has to pick again. They read differently on purpose:
 * grey for "you stopped it", red for "something went wrong".
 */
export type OutboxState = 'uploading' | 'sending' | 'stopped' | 'failed'

export interface OutboxEntry {
  messageId: string
  conversationId: string
  /** 0..1 across every file on the message, by bytes. */
  progress: number
  state: OutboxState
  /** Why it failed, in words for the user. */
  error?: string
}

interface UploadOutboxStore {
  entries: Map<string, OutboxEntry>
  put: (entry: OutboxEntry) => void
  patch: (messageId: string, patch: Partial<Omit<OutboxEntry, 'messageId' | 'conversationId'>>) => void
  drop: (messageId: string) => void
}

export const useUploadOutbox = create<UploadOutboxStore>((set) => ({
  entries: new Map(),

  put: (entry) =>
    set((prev) => {
      const entries = new Map(prev.entries)
      entries.set(entry.messageId, entry)
      return { entries }
    }),

  patch: (messageId, patch) =>
    set((prev) => {
      const current = prev.entries.get(messageId)
      if (!current) return prev
      const entries = new Map(prev.entries)
      entries.set(messageId, { ...current, ...patch })
      return { entries }
    }),

  drop: (messageId) =>
    set((prev) => {
      if (!prev.entries.has(messageId)) return prev
      const entries = new Map(prev.entries)
      entries.delete(messageId)
      return { entries }
    }),
}))

/** This message's upload, if it is one still on its way. */
export function useOutboxEntry(messageId: string): OutboxEntry | undefined {
  return useUploadOutbox((s) => s.entries.get(messageId))
}
